const DEFAULT_LOCALE = typeof Intl !== 'undefined' ? undefined : 'en-US';

export const USD_TO_TC = 1;           // 1 Tower Credit = $1.00
export const TC_TO_USD = 1 / USD_TO_TC;

export const TOWER_CREDITS_PER_USD = USD_TO_TC;
export const USD_PER_TOWER_CREDIT = TC_TO_USD;

export const usdToTc = (usd: number): number => Math.round(usd * USD_TO_TC);
export const tcToUsd = (tc: number): number => +(tc / USD_TO_TC).toFixed(2);
export const fmtUsd = (usd: number): string =>
  usd.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
export const fmtTc = (tc: number): string => `${tc.toLocaleString()} TC`;

function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function usdToCredits(usd: number | string | null | undefined): number {
  const amount = toNumber(usd);
  if (amount === null) return 0;
  return usdToTc(amount);
}

export function creditsToUsd(credits: number | string | null | undefined): number {
  const amount = toNumber(credits);
  if (amount === null) return 0;
  return tcToUsd(amount);
}

export function creditValueCentsToCredits(value: number | string | null | undefined): number {
  const cents = toNumber(value);
  if (cents === null) return 0;
  return Math.round((cents / 100) * TOWER_CREDITS_PER_USD);
}

export function creditValueCentsToUsd(value: number | string | null | undefined): number {
  const cents = toNumber(value);
  if (cents === null) return 0;
  return cents / 100;
}

export function ledgerCentsToCredits(value: number | string | null | undefined): number {
  return creditValueCentsToCredits(value);
}

export function formatCreditsValue(
  credits: number | string | null | undefined,
  options: Intl.NumberFormatOptions = {},
): string {
  const amount = toNumber(credits) ?? 0;
  const formatter = new Intl.NumberFormat(DEFAULT_LOCALE, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    ...options,
  });
  return formatter.format(amount);
}

export function formatCreditsLabel(
  credits: number | string | null | undefined,
  options: Intl.NumberFormatOptions = {},
): string {
  return `${formatCreditsValue(credits, options)} credits`;
}

export function formatUsd(
  usd: number | string | null | undefined,
  options: Intl.NumberFormatOptions = {},
): string {
  const amount = toNumber(usd) ?? 0;
  const formatter = new Intl.NumberFormat(DEFAULT_LOCALE, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  });
  return formatter.format(amount);
}

export function formatCreditsWithUsd(
  credits: number | string | null | undefined,
): { creditsLabel: string; usdLabel: string } {
  const creditAmount = toNumber(credits) ?? 0;
  const usdAmount = creditsToUsd(creditAmount);
  return {
    creditsLabel: formatCreditsLabel(creditAmount),
    usdLabel: formatUsd(usdAmount),
  };
}

export function ensureCreditIncrement(credits: number): boolean {
  return Number.isInteger(credits) && credits % 1 === 0;
}

export default {
  USD_TO_TC,
  TC_TO_USD,
  TOWER_CREDITS_PER_USD,
  USD_PER_TOWER_CREDIT,
  usdToTc,
  tcToUsd,
  fmtUsd,
  fmtTc,
  usdToCredits,
  creditsToUsd,
  creditValueCentsToCredits,
  creditValueCentsToUsd,
  ledgerCentsToCredits,
  formatCreditsValue,
  formatCreditsLabel,
  formatUsd,
  formatCreditsWithUsd,
  ensureCreditIncrement,
};
